import React, { useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuthForm } from '../hooks/useAuthForm';
import { useAuthSubmission } from '../hooks/useAuthSubmission';
import { AUTH_MESSAGES, FORM_MESSAGES } from '../constants/messages';
import { devLog } from '../utils/envUtils';

// Styles CSS pour les animations
const animationStyles = `
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes fade-in-delayed {
    0%, 20% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes slide-in {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }
  
  @keyframes bounce-gentle {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-3px); }
    60% { transform: translateY(-1px); }
  }
  
  @keyframes progress-fill {
    from { width: 0%; }
    to { width: var(--target-width, 100%); }
  }
  
  .animate-spin-slow {
    animation: spin-slow 3s linear infinite;
  }
  
  .animate-fade-in {
    animation: fade-in 1s ease-out;
  }
  
  .animate-fade-in-delayed {
    animation: fade-in-delayed 1.5s ease-out;
  }
  
  .animate-slide-in {
    animation: slide-in 0.8s ease-out both;
  }
  
  .animate-bounce-gentle {
    animation: bounce-gentle 2s ease-in-out infinite;
  }
  
  .animate-progress-fill {
    animation: progress-fill 2s ease-out both;
  }
`;

const Login = () => {
  const { state } = useApp();
  
  // Hooks personnalisés pour la logique métier
  const authForm = useAuthForm();
  const authSubmission = useAuthSubmission();

  // Rediriger si déjà connecté
  useEffect(() => {
    authSubmission.checkAuthRedirect(state.auth.isAuthenticated);
  }, [state.auth.isAuthenticated, authSubmission]);

  // Gestion de la soumission avec les nouveaux hooks

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Log uniquement en développement
    devLog('🔍 Soumission du formulaire:', {
      isRegistering: authForm.isRegistering
    });

    // Valider le formulaire
    const formValidation = authForm.validateForm();
    if (!formValidation.isValid) {
      console.error('❌ Erreur de validation:', formValidation.error);
      return;
    }
    
    // Préparer les données de soumission
    const submissionData = authForm.getSubmissionData();
    
    // Soumettre l'authentification
    const result = await authSubmission.submitAuth(authForm.isRegistering, submissionData);
    
    // Gérer le résultat
    if (!result.success) {
      devLog('🔄 Échec de soumission');
      devLog('🔄 Type d\'erreur:', result.errorType);
      // L'erreur sera affichée par le contexte d'application
    }
  };

  const handleToggleMode = () => {
    devLog('🔄 Changement de mode:', authForm.isRegistering ? 'Inscription -> Connexion' : 'Connexion -> Inscription');
    
    // Basculer le mode du formulaire
    authForm.toggleMode();
    
    devLog('🔄 Mode changé');
  };

  return (
    <>
      {/* Injection des styles d'animation */}
      <style dangerouslySetInnerHTML={{ __html: animationStyles }} />
      
      <div className="min-h-screen flex bg-gray-50">
      {/* Section présentation de l'application */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-blue-800 text-white overflow-hidden relative">
        {/* Effet de fond géométrique discret */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Formes géométriques subtiles */}
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 opacity-10 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-blue-400 opacity-15 rounded-full transform translate-x-1/3 translate-y-1/3"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-white opacity-5 rounded-full"></div>
          
          {/* Lignes subtiles */}
          <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent opacity-20"></div>
          <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent opacity-20"></div>
          
          {/* Particules discrètes */}
          <div className="absolute top-16 left-16 w-1 h-1 bg-blue-300 rounded-full opacity-40 animate-pulse"></div>
          <div className="absolute top-1/3 right-20 w-1.5 h-1.5 bg-blue-200 rounded-full opacity-30 animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-1/3 left-24 w-1 h-1 bg-blue-400 rounded-full opacity-35 animate-pulse" style={{animationDelay: '4s'}}></div>
          <div className="absolute bottom-20 right-16 w-1.5 h-1.5 bg-blue-300 rounded-full opacity-25 animate-pulse" style={{animationDelay: '6s'}}></div>
        </div>

        {/* Contenu centré */}
        <div className="relative flex items-center justify-center w-full px-8 py-16 z-10">
          <div className="max-w-lg text-center">
            {/* Animation d'horloge moderne centrée */}
            <div className="flex flex-col items-center mb-8">
              <div className="relative mb-6">
                <div className="w-20 h-20 border-4 border-blue-300 rounded-full animate-spin-slow"></div>
                <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-white rounded-full animate-spin"></div>
                <div className="absolute inset-2 w-16 h-16 bg-blue-400 rounded-full flex items-center justify-center">
                  <div className="w-1 h-6 bg-white rounded-full transform origin-bottom animate-pulse"></div>
                </div>
              </div>
              <div className="text-center">
                <h1 className="text-5xl font-bold mb-2 animate-fade-in">
                  Time Tracker
                </h1>
                <p className="text-2xl text-blue-100 animate-fade-in-delayed">
                  Suivi des temps d'équipe
                </p>
              </div>
            </div>
            <p className="text-lg text-blue-50 mb-8 leading-relaxed">
              Application de suivi des temps dépensés par projet et par membre de l'équipe, 
              avec saisie hebdomadaire simplifiée et reporting facturable.
            </p>
            
            <div className="space-y-4 text-left max-w-md mx-auto">
              <div className="flex items-start space-x-3 transform hover:scale-105 transition-all duration-300 animate-slide-in" style={{animationDelay: '0.5s'}}>
                <div className="flex-shrink-0 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mt-0.5 animate-bounce-gentle shadow-lg">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-blue-50 font-medium">Projets & phases avec dates de début/fin et budgets par phase.</p>
              </div>
              
              <div className="flex items-start space-x-3 transform hover:scale-105 transition-all duration-300 animate-slide-in" style={{animationDelay: '0.7s'}}>
                <div className="flex-shrink-0 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mt-0.5 animate-bounce-gentle shadow-lg" style={{animationDelay: '0.2s'}}>
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-blue-50 font-medium">Membres d'équipe et rôles, affectations multi‑projets.</p>
              </div>
              
              <div className="flex items-start space-x-3 transform hover:scale-105 transition-all duration-300 animate-slide-in" style={{animationDelay: '0.9s'}}>
                <div className="flex-shrink-0 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mt-0.5 animate-bounce-gentle shadow-lg" style={{animationDelay: '0.4s'}}>
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-blue-50 font-medium">Saisie hebdomadaire rapide par projet, facturable/non facturable.</p>
              </div>
              
              <div className="flex items-start space-x-3 transform hover:scale-105 transition-all duration-300 animate-slide-in" style={{animationDelay: '1.1s'}}>
                <div className="flex-shrink-0 w-6 h-6 bg-blue-400 rounded-full flex items-center justify-center mt-0.5 animate-bounce-gentle shadow-lg" style={{animationDelay: '0.6s'}}>
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-blue-50 font-medium">Rapports & KPIs d'avancement, consommation de budget par phase, et taux facturable.</p>
              </div>
            </div>

            {/* Barres de progression animées */}
            <div className="mt-10 max-w-xs mx-auto">
              <div className="text-sm text-blue-200 mb-4 font-medium text-center">Progression des projets en temps réel</div>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-blue-100 w-20 font-medium">Projet A</div>
                  <div className="flex-1 bg-blue-800 bg-opacity-50 rounded-full h-2.5 overflow-hidden shadow-inner">
                    <div className="bg-gradient-to-r from-blue-400 to-blue-300 h-full rounded-full animate-progress-fill shadow-sm" style={{width: '75%'}}></div>
                  </div>
                  <div className="text-sm text-blue-200 w-10 text-right font-semibold">75%</div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-blue-100 w-20 font-medium">Projet B</div>
                  <div className="flex-1 bg-blue-800 bg-opacity-50 rounded-full h-2.5 overflow-hidden shadow-inner">
                    <div className="bg-gradient-to-r from-green-400 to-green-300 h-full rounded-full animate-progress-fill shadow-sm" style={{width: '45%', animationDelay: '0.5s'}}></div>
                  </div>
                  <div className="text-sm text-blue-200 w-10 text-right font-semibold">45%</div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-blue-100 w-20 font-medium">Projet C</div>
                  <div className="flex-1 bg-blue-800 bg-opacity-50 rounded-full h-2.5 overflow-hidden shadow-inner">
                    <div className="bg-gradient-to-r from-purple-400 to-purple-300 h-full rounded-full animate-progress-fill shadow-sm" style={{width: '90%', animationDelay: '1s'}}></div>
                  </div>
                  <div className="text-sm text-blue-200 w-10 text-right font-semibold">90%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section formulaire de connexion */}
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {authForm.isRegistering ? AUTH_MESSAGES.ACCOUNT_CREATION_TITLE : AUTH_MESSAGES.LOGIN_TITLE}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {authForm.isRegistering ? AUTH_MESSAGES.ACCOUNT_CREATION_SUBTITLE : AUTH_MESSAGES.LOGIN_SUBTITLE}
            </p>
          </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            {authForm.isRegistering && (
              <>
                <div>
                  <label htmlFor="nom" className="sr-only">
                    {FORM_MESSAGES.LABEL_LASTNAME}
                  </label>
                  <input
                    id="nom"
                    name="nom"
                    type="text"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder={FORM_MESSAGES.PLACEHOLDER_LASTNAME}
                    value={authForm.formData.nom}
                    onChange={authForm.handleInputChange}
                  />
                </div>
                <div>
                  <label htmlFor="prenom" className="sr-only">
                    {FORM_MESSAGES.LABEL_FIRSTNAME}
                  </label>
                  <input
                    id="prenom"
                    name="prenom"
                    type="text"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder={FORM_MESSAGES.PLACEHOLDER_FIRSTNAME}
                    value={authForm.formData.prenom}
                    onChange={authForm.handleInputChange}
                  />
                </div>
              </>
            )}
            
            <div>
              <label htmlFor="email" className="sr-only">
                {FORM_MESSAGES.LABEL_EMAIL}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className={`appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 ${
                  !authForm.isRegistering ? 'rounded-t-md' : ''
                } focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm`}
                placeholder={FORM_MESSAGES.PLACEHOLDER_EMAIL}
                value={authForm.formData.email}
                onChange={authForm.handleInputChange}
              />
            </div>
            
            <div>
              <label htmlFor="mot_de_passe" className="sr-only">
                {FORM_MESSAGES.LABEL_PASSWORD}
              </label>
              <input
                id="mot_de_passe"
                name="mot_de_passe"
                type="password"
                autoComplete={authForm.isRegistering ? "new-password" : "current-password"}
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={FORM_MESSAGES.PLACEHOLDER_PASSWORD}
                value={authForm.formData.mot_de_passe}
                onChange={authForm.handleInputChange}
              />
            </div>
          </div>



          <div>
            <button
              type="submit"
              disabled={state.auth.loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state.auth.loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {AUTH_MESSAGES.LOADING}
                </div>
              ) : (
                authForm.isRegistering ? AUTH_MESSAGES.CREATE_ACCOUNT_BUTTON : AUTH_MESSAGES.LOGIN_BUTTON
              )}
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleToggleMode}
              className="text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              {authForm.isRegistering 
                ? AUTH_MESSAGES.ALREADY_HAVE_ACCOUNT
                : AUTH_MESSAGES.NO_ACCOUNT_YET
              }
            </button>
          </div>
        </form>

        {/* Affichage des erreurs */}
        {state.error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  {state.error}
                </h3>
              </div>
            </div>
          </div>
        )}

        {/* Message informatif pour les nouveaux utilisateurs */}
        {authForm.isRegistering && (
          <div className="rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {AUTH_MESSAGES.FREE_ACCOUNT_TITLE}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {AUTH_MESSAGES.FREE_ACCOUNT_DESCRIPTION}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

          {/* Version mobile de la présentation (en bas du formulaire) */}
          <div className="lg:hidden mt-8 p-6 bg-blue-50 rounded-lg animate-fade-in border border-blue-100">
            <div className="flex items-center mb-3">
              <div className="relative mr-3">
                <div className="w-8 h-8 border-2 border-blue-400 rounded-full animate-spin-slow"></div>
                <div className="absolute inset-0 w-8 h-8 border-2 border-transparent border-t-blue-600 rounded-full animate-spin"></div>
              </div>
              <h3 className="text-lg font-semibold text-blue-900">
                Time Tracker — Suivi des temps d'équipe
              </h3>
            </div>
            <p className="text-sm text-blue-700 mb-4">
              Application de suivi des temps dépensés par projet et par membre de l'équipe, 
              avec saisie hebdomadaire simplifiée et reporting facturable.
            </p>
            <div className="space-y-2 text-sm text-blue-600">
              <p className="animate-slide-in" style={{animationDelay: '0.2s'}}>• Projets & phases avec dates de début/fin et budgets par phase</p>
              <p className="animate-slide-in" style={{animationDelay: '0.4s'}}>• Membres d'équipe et rôles, affectations multi‑projets</p>
              <p className="animate-slide-in" style={{animationDelay: '0.6s'}}>• Saisie hebdomadaire rapide par projet, facturable/non facturable</p>
              <p className="animate-slide-in" style={{animationDelay: '0.8s'}}>• Rapports & KPIs d'avancement, consommation de budget par phase, et taux facturable</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login; 